import { makeAutoObservable } from 'mobx'
import type { IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowBusinessHoursStep } from 'entities/SmartCall/api/types'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import {
  allSmartCallFlowActionTypes,
  type ISmartCallFlowActionType,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'
import { InboxesPath } from 'pages/settings/pages/inboxes/route/inboxesPath.ts'

export type IBusinessHoursStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowBusinessHoursStep['args']
}

export class BusinessHoursStore {
  static readonly type = 'businessHours' satisfies ISmartCallFlowActionType
  static title = 'Business hours'
  static iconProps: IIconProps = {
    icon: 'clock',
    color: 'var(--custom-green-custom)',
  }
  public readonly actionStore: ISmartCallActionStore

  private readonly _branchLabels: Record<'during_hours' | 'after_hours', string> = {
    during_hours: 'During hours',
    after_hours: 'After hours',
  }

  getBranchNameByTargetId = (targetId: string): string => {
    const nextIds = this.actionStore.nextIds
    if (targetId === nextIds[0]) return this._branchLabels.during_hours
    if (targetId === nextIds[1]) return this._branchLabels.after_hours
    return ''
  }

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: IBusinessHoursStoreProps,
    private _parent?: { inbox: Inbox | null }
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: BusinessHoursStore.type,
      name: BusinessHoursStore.title,
      allowedNextActions: allSmartCallFlowActionTypes.filter((type) => type !== 'businessHours'),
      allowedBeforeActions: allSmartCallFlowActionTypes.filter(
        (type) => type !== 'ivrPhoneTree' && type !== 'businessHours'
      ),
      edgeType: 'customEdgeWithLabel',
    })

    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowBusinessHoursStep, 'id' | 'type'> = {
    id: 'businessHours',
    type: 'action',
  }

  get step(): ISmartCallFlowBusinessHoursStep {
    const nextIds = this.actionStore.nextIds

    return {
      ...this._stepIdType,
      args: {
        next_on_during_hours: nextIds[0] ?? '',
        next_on_after_hours: nextIds[1] ?? '',
      },
      next: nextIds[0] ?? '',
    }
  }

  setApiError = (key: string, message: string) => {}

  validate = () => {
    if (this.isConfigurationError) return false
    return true
  }

  get isBusinessHoursActive() {
    return this._parent?.inbox?.origin.business_hours?.is_active
  }
  get businessHoursLink() {
    if (!this._parent?.inbox?.id) return ''
    return `/${InboxesPath.businessHours(this._parent?.inbox?.id)}`
  }

  get isConfigurationError() {
    if (!this.isBusinessHoursActive) {
      return true
    }
    return false
  }
}
